import {Button, Center, Field, Input, Portal, Select, Stack, Textarea} from "@chakra-ui/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useRecoilState} from "recoil";
import {profileData} from "../../components/hooks/recoilData";
import {useToast} from "@chakra-ui/toast";
import {userApi} from "../../api/user-api";
import {DateTime} from "luxon";
import {Tooltip} from "../../components/Tooltip";
import {countryList, sexList} from "../Home/RegForm";
import {errorNotify} from "../../components/hooks/CommonUtils";

interface ProfileExt {
    nickName: string,
    firstName: string,
    secondName: string,
    lastName: string,
    description: string,
    tel: string,
    country: string,
    sex: number,
    dateOfBirth: string,
    passNumber: string,
    passDate: string,
    passDepartmentCode: string,
    passDepartment: string,
    passPlaceOfBirth: string,
    passAddress: string,
    codeInn: string,
    codeKpp: string,
    codeSnils: string,
}

export const Profile = () => {
    const inputDefault: ProfileExt = useMemo(() => {
        return {
            nickName: "",
            firstName: "",
            secondName: "",
            lastName: "",
            description: "",
            tel: "",
            country: "RU",
            sex: 0,
            dateOfBirth: "",
            passNumber: "",
            passDate: "",
            passDepartmentCode: "",
            passDepartment: "",
            passPlaceOfBirth: "",
            passAddress: "",
            codeInn: "",
            codeKpp: "",
            codeSnils: "",
        }
    }, []);
    const [inputParams, setInputParams] = useState<ProfileExt>(inputDefault);
    const toast = useToast();
    const [profile, setProfile] = useRecoilState(profileData);
    useEffect(() => {
        if (profile) {
            setInputParams({
                nickName: profile.NICK_NAME ? profile.NICK_NAME : "",
                firstName: profile.FIRST_NAME ? profile.FIRST_NAME : "",
                secondName: profile.SECOND_NAME ? profile.SECOND_NAME : "",
                lastName: profile.LAST_NAME ? profile.LAST_NAME : "",
                description: profile.DESCRIPTION ? profile.DESCRIPTION : "",
                tel: profile.TEL ? profile.TEL : "",
                country: profile.COUNTRY ? profile.COUNTRY : "RU",
                sex: profile.SEX ? profile.SEX : 0,
                dateOfBirth: profile.DATE_OF_BIRTH ? DateTime.fromMillis(profile.DATE_OF_BIRTH).toFormat('dd.MM.yyyy') : "",
                passNumber: profile.PASS_NUMBER ? profile.PASS_NUMBER : "",
                passDate: profile.PASS_DATE ? DateTime.fromMillis(profile.PASS_DATE).toFormat('dd.MM.yyyy') : "",
                passDepartmentCode: profile.PASS_DEPARTMENT_CODE ? profile.PASS_DEPARTMENT_CODE : "",
                passDepartment: profile.PASS_DEPARTMENT ? profile.PASS_DEPARTMENT : "",
                passPlaceOfBirth: profile.PASS_PLACE_OF_BIRTH ? profile.PASS_PLACE_OF_BIRTH : "",
                passAddress: profile.PASS_ADDRESS ? profile.PASS_ADDRESS : "",
                codeInn: profile.CODE_INN ? profile.CODE_INN.trim() : "",
                codeKpp: profile.CODE_KPP ? profile.CODE_KPP.trim() : "",
                codeSnils: profile.CODE_SNILS ? profile.CODE_SNILS.trim() : "",
            });
        }
    }, [profile]);
    const save = useCallback(() => {
        userApi.profileOwnEdit(inputParams.nickName, inputParams.firstName, inputParams.secondName, inputParams.lastName,
            inputParams.description, inputParams.tel, inputParams.country, '', inputParams.sex, DateTime.fromFormat(inputParams.dateOfBirth, 'dd.MM.yyyy').toMillis(),
            inputParams.passNumber, DateTime.fromFormat(inputParams.passDate, 'dd.MM.yyyy').toMillis(),
            inputParams.passDepartmentCode, inputParams.passDepartment, inputParams.passPlaceOfBirth, inputParams.passAddress, inputParams.codeInn,
            inputParams.codeKpp, inputParams.codeSnils)
            .then((o) => {
                if (o) {
                    toast({title: "Save", status: "success"})
                    // setInputParams(inputDefault);
                    userApi.profileOwnGet()
                        .then(o2 => setProfile(o2))
                        .catch((err) => {
                            console.log("Error status code is: " + err)
                        });
                } else {
                    toast({title: "Save failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParams, toast, setProfile]);

    return (
        <Center>
            <Stack>
                <Field.Root orientation="horizontal">
                    <Field.Label>Псевдоним</Field.Label>
                    <Input type="text" placeholder="Псевдоним"
                           value={inputParams.nickName}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, nickName: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>Имя</Field.Label>
                    <Input type="text" placeholder="Имя"
                           value={inputParams.firstName}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, firstName: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>Отчество</Field.Label>
                    <Input type="text" placeholder="Отчество"
                           value={inputParams.secondName}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, secondName: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>Фамилия</Field.Label>
                    <Input type="text" placeholder="Фамилия"
                           value={inputParams.lastName}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, lastName: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>Описание</Field.Label>
                    <Textarea placeholder="Описание"
                              value={inputParams.description}
                              onChange={(it) => setInputParams((prev) => {
                                  return {...prev, description: it.target.value}
                              })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Телефон <Field.RequiredIndicator/></Field.Label>
                    <Input type="tel" placeholder="Телефон"
                           value={inputParams.tel}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, tel: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Страна <Field.RequiredIndicator/></Field.Label>
                    <Select.Root
                        value={[String(inputParams.country)]}
                        // defaultValue={[String(inputParams.country)]}
                        collection={countryList} size="sm" width="320px"
                        onValueChange={(e: any) => setInputParams(prev => {
                            return {
                                ...prev, country: e.value[0]
                            }
                        })}>
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder="Страна"/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {countryList.items.map((it: any) => (
                                        <Select.Item item={it} key={it.value}>
                                            {it.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Пол <Field.RequiredIndicator/></Field.Label>
                    <Select.Root
                        value={[String(inputParams.sex)]}
                        // defaultValue={[String(inputParams.sex)]}
                        collection={sexList} size="sm" width="320px"
                        onValueChange={(e: any) => setInputParams(prev => {
                            return {
                                ...prev, sex: parseInt(e.value[0])
                            }
                        })}>
                        <Select.HiddenSelect/>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder="Пол"/>
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator/>
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {sexList.items.map((it: any) => (
                                        <Select.Item item={it} key={it.value}>
                                            {it.label}
                                            <Select.ItemIndicator/>
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Tooltip showArrow content="date, format dd.mm.yyyy">
                        <Field.Label>Дата рождения <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата рождения"
                           value={inputParams.dateOfBirth}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, dateOfBirth: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Серия и номер паспорта <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Серия и номер паспорта"
                           value={inputParams.passNumber}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passNumber: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Tooltip showArrow content="date, format dd.mm.yyyy">
                        <Field.Label>Дата выдачи паспорта <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата выдачи паспорта"
                           value={inputParams.passDate}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passDate: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Код подразделения, кто выдал <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Код подразделения, кто выдал"
                           value={inputParams.passDepartmentCode}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passDepartmentCode: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Когда выдал <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Когда выдал"
                           value={inputParams.passDepartment}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passDepartment: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Место рождения <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Место рождения"
                           value={inputParams.passPlaceOfBirth}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passPlaceOfBirth: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>Адрес <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="Адрес"
                           value={inputParams.passAddress}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, passAddress: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal" required>
                    <Field.Label>ИНН <Field.RequiredIndicator/></Field.Label>
                    <Input type="text" placeholder="ИНН"
                           value={inputParams.codeInn}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, codeInn: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>КПП</Field.Label>
                    <Input type="text" placeholder="КПП"
                           value={inputParams.codeKpp}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, codeKpp: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Field.Label>СНИЛС</Field.Label>
                    <Input type="text" placeholder="СНИЛС"
                           value={inputParams.codeSnils}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, codeSnils: it.target.value}
                           })}/>
                </Field.Root>
                <Button borderRadius={0} variant="solid" colorScheme="teal" width="full" onClick={save}>
                    Сохранить
                </Button>
            </Stack>
        </Center>
    );
};